import { create15Times, Dow, RecurrentSchedule, Reservation } from '@raysee/schema';
import dayjs from 'dayjs';
import { useEffect, VFC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  Checkbox,
  CheckboxGroup,
  ControlLabel,
  DatePicker,
  Form,
  FormGroup,
  HelpBlock,
  Modal,
  Radio,
  RadioGroup,
  SelectPicker,
} from 'rsuite';
import { notifyScheduleError } from '../../utils/scheduleError';

type FieldValues = Pick<RecurrentSchedule, 'startDate' | 'endDate' | 'dows'> & {
  startTime: number;
  endTime: number;
  hasExpiration: boolean;
};

interface Props {
  recurrentSchedule: RecurrentSchedule;
  reservations: Reservation[];
  isOpen: boolean;
  onClose: () => void;
  onEdit: (values: FieldValues) => Promise<void>;
}

export const EditRecurrentScheduleModal: VFC<Props> = (props) => {
  const {
    control,
    getValues,
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FieldValues>();

  const selectedStartTime = watch('startTime');
  const selectedEndTime = watch('endTime');
  const selectedDows = watch('dows');
  const selectedStartDate = watch('startDate');
  const hasExpiration = watch('hasExpiration');
  const selectedEndDate = watch('endDate');

  // フォームが有効かどうかを確認
  const isFormValid =
    selectedStartTime != null &&
    !!selectedEndTime &&
    !!selectedStartDate &&
    !!selectedDows?.length &&
    (!hasExpiration || !!selectedEndDate);

  // 開始時間が変更されたら終了時間をリセット
  useEffect(() => {
    if (selectedStartTime != null) {
      setValue('endTime', undefined);
    }
  }, [selectedStartTime, setValue]);

  // 開始は00:00~23:45
  const startStarted = dayjs().startOf('day');
  const startEnded = dayjs().startOf('day').add(1, 'day').subtract(15, 'minute');
  const startTimes = create15Times(startStarted, startEnded);

  // 終了は00:15~翌06:00
  const endStarted = dayjs().startOf('day').add(15, 'minute');
  const endEnded = dayjs().startOf('day').add(1, 'day').add(6, 'hour');
  const endTimes = create15Times(endStarted, endEnded);

  // 開始時間に基づいて終了時間をフィルタリング
  const filteredEndTimes =
    selectedStartTime != null
      ? endTimes.filter((time) => {
          // 開始時間より大きい値のみを表示
          if (time.value <= selectedStartTime) return false;

          // 開始時間と同じ分数のものだけを表示（例：10:15なら11:15, 12:15など）
          const startMinutes = selectedStartTime % 60;
          const endMinutes = time.value % 60;
          return startMinutes === endMinutes;
        })
      : [];

  const submit = async ({
    startTime,
    endTime,
    startDate,
    endDate,
    dows,
    hasExpiration,
  }: FieldValues) => {
    if (startTime < 0 || !endTime || !startDate || !endTime || dows.length === 0) {
      alert('未入力の項目があります');
      return;
    }

    if (hasExpiration && startDate >= endDate) {
      alert('終了日は開始日以降にしてください');
      return;
    }

    if (startTime >= endTime) {
      alert('終了時間は開始時間以降にしてください');
      return;
    }

    if (endTime - startTime > 24 * 60) {
      alert('1日最大24時間までとなります');
      return;
    }

    const beforeReservations = props.reservations
      .filter((reservation) =>
        // 予約日が変更前の定期スケジュールの期間内かチェック
        dayjs(reservation.startTime).isBetween(
          dayjs(props.recurrentSchedule.startDate),
          dayjs(props.recurrentSchedule.endDate),
          null,
          '[]',
        ),
      )
      .filter((reservation) => {
        // 予約の曜日が変更前の定期スケジュールの曜日に含まれているかチェック
        const reservationDow = dayjs(reservation.startTime).day();
        const dowMap = ['日', '月', '火', '水', '木', '金', '土'];
        const dowString = dowMap[reservationDow];
        // 繰り返しで設定しているものと同じ曜日かつ開始時間が重複しているものを除く
        const reservationStartTime = dayjs(reservation.startTime).diff(
          dayjs(reservation.startTime).startOf('day'),
          'minute',
        );
        const reservationEndTime = reservationStartTime + 60;
        return !(
          props.recurrentSchedule.dows.includes(dowString as Dow) &&
          reservationStartTime >= startTime &&
          reservationEndTime <= endTime
        );
      });

    if (beforeReservations.length > 0) {
      const reservationList = beforeReservations
        .map(
          (r) => `・${dayjs(r.startTime).format('YYYY年MM月DD日(ddd) HH:mm')}開始 ${r.user.name}`,
        )
        .join('\n');
      alert(
        `変更前の時間帯に予約が残っています。キャンセル後に再度お試しください。\n\n${reservationList}`,
      );
      return;
    }

    try {
      await props.onEdit({
        startDate: startDate,
        endDate: endDate,
        startTime: startTime,
        endTime: endTime,
        dows: dows,
        hasExpiration: hasExpiration,
      });
    } catch (error) {
      notifyScheduleError('繰り返しシフト変更', error);
      return;
    }
    props.onClose();
  };

  return (
    <Modal show={props.isOpen} onHide={props.onClose}>
      <Modal.Header>
        <Modal.Title>繰り返しシフトの変更</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="pb-4">以下の設定を変更します</div>
        {props.recurrentSchedule && (
          <Form fluid>
            <FormGroup>
              <ControlLabel>開始日</ControlLabel>
              <Controller
                name="startDate"
                defaultValue={props.recurrentSchedule.startDate}
                control={control}
                rules={{ required: { value: true, message: '必須です' } }}
                render={({ field }) => <DatePicker {...field} />}
              />
              {errors.startDate && (
                <HelpBlock className="text-red-500">
                  <span className="text-red-500">{errors.startDate.message}</span>
                </HelpBlock>
              )}
            </FormGroup>
            <FormGroup>
              <ControlLabel>終了日</ControlLabel>
              <Controller
                name="hasExpiration"
                control={control}
                defaultValue={!!props.recurrentSchedule.endDate}
                render={({ field }) => (
                  <>
                    <RadioGroup {...field}>
                      <Radio value={false}>なし</Radio>
                      <Radio value={true}>終了日</Radio>
                    </RadioGroup>
                    <Controller
                      name="endDate"
                      control={control}
                      defaultValue={props.recurrentSchedule.endDate}
                      render={({ field }) => (
                        <DatePicker
                          defaultValue={props.recurrentSchedule.endDate}
                          disabled={!getValues().hasExpiration}
                          {...field}
                        />
                      )}
                    />
                  </>
                )}
              />
            </FormGroup>
            <div className="flex gap-2">
              <FormGroup>
                <ControlLabel>開始時刻</ControlLabel>
                <Controller
                  name="startTime"
                  control={control}
                  rules={{ required: { value: true, message: '必須です' } }}
                  render={({ field }) => (
                    <SelectPicker
                      data={startTimes}
                      valueKey="value"
                      placeholder="HH:MM"
                      searchable={false}
                      {...field}
                    />
                  )}
                />
                {errors.startTime && (
                  <HelpBlock>
                    <span className="text-red-500">{errors.startTime.message}</span>
                  </HelpBlock>
                )}
              </FormGroup>
              <FormGroup>
                <ControlLabel>終了時刻</ControlLabel>
                <Controller
                  name="endTime"
                  control={control}
                  defaultValue={
                    dayjs(props.recurrentSchedule.startTime).isSame(
                      props.recurrentSchedule.endTime,
                      'day',
                    )
                      ? props.recurrentSchedule.endTime.getHours() * 60 +
                        props.recurrentSchedule.endTime.getMinutes()
                      : props.recurrentSchedule.endTime.getHours() * 60 +
                        props.recurrentSchedule.endTime.getMinutes() +
                        1440
                  }
                  rules={{ required: { value: true, message: '必須です' } }}
                  render={({ field }) => (
                    <SelectPicker
                      data={filteredEndTimes}
                      valueKey="value"
                      placeholder="HH:MM"
                      searchable={false}
                      disabled={selectedStartTime == null}
                      locale={{ noResultsText: '開始時間を選んでください' }}
                      {...field}
                    />
                  )}
                />
                {errors.endTime && (
                  <HelpBlock>
                    <span className="text-red-500">{errors.endTime.message}</span>
                  </HelpBlock>
                )}
              </FormGroup>
            </div>
            <FormGroup>
              <ControlLabel>曜日</ControlLabel>
              <Controller
                name="dows"
                control={control}
                defaultValue={props.recurrentSchedule.dows}
                rules={{
                  validate: () => {
                    if (props.recurrentSchedule.dows === undefined) {
                      return '必須です';
                    }

                    if (props.recurrentSchedule.dows.filter((dow) => !!dow).length < 1) {
                      return '必須です';
                    }

                    return true;
                  },
                }}
                render={({ field }) => (
                  <CheckboxGroup className="flex" {...field}>
                    <Checkbox value="日">日</Checkbox>
                    <Checkbox value="月">月</Checkbox>
                    <Checkbox value="火">火</Checkbox>
                    <Checkbox value="水">水</Checkbox>
                    <Checkbox value="木">木</Checkbox>
                    <Checkbox value="金">金</Checkbox>
                    <Checkbox value="土">土</Checkbox>
                  </CheckboxGroup>
                )}
              />
              {errors.dows && (
                <HelpBlock>
                  <span className="text-red-500">{(errors.dows as any).message}</span>
                </HelpBlock>
              )}
            </FormGroup>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          appearance="primary"
          color="blue"
          onClick={handleSubmit(submit)}
          // 変更完了までモーダルを開いたままにするため、二重送信を防ぐ
          disabled={!isFormValid || isSubmitting}
        >
          {isSubmitting ? '変更中...' : '変更する'}
        </Button>
        {/* ↑だとローカルでエラーになるので、ローカル環境だけ↓にしてます */}
        {/* <Button
          onClick={() => submit(getValues())}
          appearance="primary"
          disabled={!isFormValid}
        >
          変更する
        </Button> */}
        <Button onClick={props.onClose} appearance="subtle">
          キャンセル
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
